import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Dialog, InputAdornment, TextField, Typography } from '@material-ui/core';
import { ButtonClearInput, CustomButton, CustomVacantButton } from '../button';
import { AddressSearchDialog } from '../address';
import tokens from '../theme/tokens';

// 의뢰자 정보 설정 모달 — 마이페이지. 이름·연락처는 회원 정보(수정 불가)로 보여 주고 상호·부서명·주소·상세주소만 받는다.
// 저장 전에는 마지막 접수 값(또는 회원 정보)이 미리 채워져 있다. onSave(values) → { ok, message }
const useStyles = makeStyles((theme) => ({
  paper: {
    width: 480,
    maxWidth: 'calc(100% - 32px)',
    padding: '32px 32px 28px',
    borderRadius: tokens.radiusLg,
    boxShadow: tokens.dialogShadow,
    [theme.breakpoints.down('sm')]: { padding: '28px 20px 24px' },
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: -1,
    color: tokens.ink,
  },
  fixed: {
    display: 'flex',
    gap: 10,
    marginTop: 18,
    padding: '12px 14px',
    borderRadius: 12,
    backgroundColor: tokens.primarySoft,
  },
  fixedItem: {
    flex: '1 1 0',
    minWidth: 0,
    '& small': { display: 'block', fontSize: 12, color: tokens.muted, letterSpacing: -0.4 },
    '& b': { display: 'block', marginTop: 2, fontSize: 15, color: tokens.ink, letterSpacing: -0.6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  },
  fields: {
    marginTop: 18,
    '& .MuiInputBase-root': {
      boxShadow: 'none',
      marginBottom: 12,
      width: '100%',
      padding: '0 16px',
      borderRadius: tokens.radius,
      border: `1px solid ${tokens.line}`,
      backgroundColor: tokens.surface,
      '& input': { height: 50, '&::-webkit-input-placeholder': { color: tokens.placeholder } },
      '& .MuiOutlinedInput-input': { padding: 0 },
    },
    '& .Mui-disabled': { borderColor: 'transparent !important', color: tokens.ink },
    '& .MuiOutlinedInput-notchedOutline': { borderRadius: `${tokens.radius}px !important` },
  },
  address: {
    cursor: 'pointer',
    '& input': { cursor: 'pointer' },
  },
  error: {
    marginTop: 2,
    fontSize: 13,
    color: tokens.primaryDark,
  },
  foot: {
    display: 'flex',
    gap: 10,
    marginTop: 14,
    '& > *': { flex: '1 1 0', height: 48, fontSize: 16 },
  },
}));

const FIELDS = [
  { name: 'company', placeholder: '상호', autoComplete: 'organization' },
  { name: 'dept', placeholder: '부서명', autoComplete: 'organization-title' },
];

function RequesterSettingsDialog({ open, user, onClose, onSave }) {
  const cs = useStyles();
  const [form, setForm] = useState({ company: '', dept: '', addr: '', addrDtl: '', bcode: '' });
  const [addrOpen, setAddrOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // 열 때마다 회원의 현재 의뢰자 정보(직접 저장한 값 또는 마지막 접수 값)로 채운다
  useEffect(() => {
    if (!open) return;
    const r = (user && user.requester) || {};
    setForm({ company: r.company || '', dept: r.dept || '', addr: r.addr || '', addrDtl: r.addrDtl || '', bcode: r.bcode || '' });
    setError('');
  }, [open, user]);

  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const selectAddress = (sel) => { setForm((f) => ({ ...f, addr: sel.address, bcode: sel.bcode || '' })); setAddrOpen(false); };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    const r = await onSave({ company: form.company.trim(), dept: form.dept.trim(), addr: form.addr, addrDtl: form.addrDtl.trim(), bcode: form.bcode });
    setBusy(false);
    if (!r || !r.ok) { setError((r && r.message) || '저장하지 못했습니다.'); return; }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: cs.paper }} aria-labelledby="requester-settings-title">
      <AddressSearchDialog open={addrOpen} type="requester" handleClose={() => setAddrOpen(false)} onSelect={selectAddress} />
      <Typography id="requester-settings-title" className={cs.title}>의뢰자 정보 설정</Typography>
      <div className={cs.fixed}>
        <div className={cs.fixedItem}><small>담당자명</small><b>{(user && user.name) || '-'}</b></div>
        <div className={cs.fixedItem}><small>연락처</small><b>{(user && user.phone) || '-'}</b></div>
      </div>
      <div className={cs.fields}>
        {FIELDS.map((f) => (
          <TextField
            key={f.name}
            fullWidth
            variant="outlined"
            name={f.name}
            placeholder={f.placeholder}
            value={form[f.name]}
            inputProps={{ autoComplete: f.autoComplete, maxLength: 100 }}
            onChange={change}
          />
        ))}
        <TextField
          fullWidth
          disabled
          variant="outlined"
          className={cs.address}
          placeholder="주소"
          value={form.addr}
          onClick={() => setAddrOpen(true)}
          InputProps={{ endAdornment: <InputAdornment position="end"><ButtonClearInput /></InputAdornment> }}
        />
        <TextField
          fullWidth
          variant="outlined"
          name="addrDtl"
          placeholder="상세주소"
          value={form.addrDtl}
          inputProps={{ maxLength: 200 }}
          onChange={change}
        />
        {error && <Typography className={cs.error}>{error}</Typography>}
      </div>
      <div className={cs.foot}>
        <CustomVacantButton variant="contained" color="primary" onClick={onClose}>닫기</CustomVacantButton>
        <CustomButton variant="contained" color="primary" disabled={busy} onClick={save}>{busy ? '저장 중' : '저장'}</CustomButton>
      </div>
    </Dialog>
  );
}

export default RequesterSettingsDialog;
